import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { SearchField } from '@/design-system/search-field';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import {
  Hub,
  HUB_STATUS_LABELS,
  HubStatusFilter,
  hubsAPI,
} from '@/services/api/hubs';
import { useUrlParam, useUrlSearch } from '@/hooks/urlState';

import HubFormModal from './components/HubFormModal';

type TabKey = HubStatusFilter;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'all', label: 'All' },
];

const TAB_KEYS = TABS.map((t) => t.key) as readonly TabKey[];
// Switching a status tab clears the search term (per-tab), keeping no other
// filters (this page has none). Stable identity — declared at module scope.
const TAB_RESET = ['q'] as const;

const HubsPage: React.FC = () => {
  // Tab + search live in the URL (shareable, back-button aware). No pagination
  // on this page, so no page-reset. Tab switches use push history and drop `q`.
  const [tab, setTab] = useUrlParam<TabKey>('tab', 'active', {
    allowed: TAB_KEYS,
    history: 'push',
    resetParams: TAB_RESET,
  });
  // Search is client-side; the URL is the source of truth and the box resyncs
  // when the URL changes externally (tab switch / Back).
  const { value: search, setValue: setSearch, committed } = useUrlSearch('q', {
    delay: 200,
  });
  const debouncedSearch = committed.toLowerCase();

  const [items, setItems] = useState<Hub[]>([]);
  const [tableLoading, setTableLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingHub, setEditingHub] = useState<Hub | null>(null);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    try {
      const data = await hubsAPI.list(tab);
      setItems(data);
    } catch (err) {
      setItems([]);
      toast.error('Could not load hubs', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTableLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredItems = useMemo(() => {
    if (!debouncedSearch) return items;
    return items.filter((hub) => {
      const haystack = [hub.code, hub.name, hub.address ?? '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(debouncedSearch);
    });
  }, [items, debouncedSearch]);

  const handleSaved = (hub: Hub) => {
    setItems((prev) => {
      const idx = prev.findIndex((h) => h.id === hub.id);
      if (idx === -1) {
        const next = [...prev, hub];
        next.sort((a, b) => a.code.localeCompare(b.code));
        return next;
      }
      const next = [...prev];
      next[idx] = hub;
      return next;
    });
    if (tab !== 'all' && hub.status !== tab) {
      fetchList();
    }
  };

  const openCreate = () => {
    setEditingHub(null);
    setFormOpen(true);
  };

  const openEdit = (hub: Hub) => {
    setEditingHub(hub);
    setFormOpen(true);
  };

  const codeCls =
    'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
  const nameCls = 'text-[13px] text-text-strong-950 tracking-tight';
  const metaCls = 'text-[11px] text-text-sub-600';

  const columns: TableDataColumn<Hub>[] = [
    {
      key: 'code',
      title: 'Code',
      render: (_v, record) => <span className={codeCls}>{record.code}</span>,
    },
    {
      key: 'name',
      title: 'Name / address',
      render: (_v, record) => (
        <div>
          <p className={nameCls}>{record.name}</p>
          <p className={`${metaCls} truncate max-w-[360px]`}>
            {record.address ?? '—'}
          </p>
        </div>
      ),
    },
    {
      key: 'coords',
      title: 'Coordinates',
      render: (_v, record) => (
        <span className="font-mono text-[12px] text-text-sub-600 tabular-nums">
          {record.latitude.toFixed(6)}, {record.longitude.toFixed(6)}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => (
        <Badge
          status={record.status === 'active' ? 'success' : 'neutral'}
          type="dot"
        >
          {HUB_STATUS_LABELS[record.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, record) => (
        <Button
          tone="primary"
          style="stroke"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            openEdit(record);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">
            <span className="text-accent font-bold mr-3">02</span>
            Master data · Hub
          </p>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Hubs
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            Source of truth for every Dash facility. Zones, invoices, batches,
            and manifests all reference hubs by their stable code.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button tone="neutral" style="stroke" onClick={fetchList}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
          <Button onClick={openCreate}>
            <i className="ri-add-line mr-1.5 text-[16px]" />
            New hub
          </Button>
        </div>
      </div>

      <Card padding="none">
        {/* items-end so the active-tab underline sits on the card hairline */}
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList variant="line" className="w-auto border-0">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-1 items-center justify-end gap-2 lg:py-2">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search code, name, address…"
              size="sm"
              className="max-w-xs flex-1"
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<Hub>
            data={filteredItems}
            columns={columns}
            loading={tableLoading}
            size="sm"
          />
        </div>
      </Card>

      <HubFormModal
        isOpen={formOpen}
        hub={editingHub}
        onClose={() => {
          setFormOpen(false);
          setEditingHub(null);
        }}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default HubsPage;
